// web_components/wcConsentModal/wcConsentModal.ts

import { WebComponent_Base } from '../../WebComponent_Base';
import { addEventListener, removeEventListener } from '../../../utils/events';
import { renderProductHTML } from './wcConsentModal_Renderer';
import { ConsentUpdate } from '../../../consent/consentManager';
import { applyFocusTrap } from '../../../utils/focusTrap';
import { dispatchConsentUpdate } from '../../../events/consentUpdateEvent';
import { WebComponent_Utils } from '../../WebComponent_Utils';

/**
 * A custom web component for displaying a cookie consent modal.
 * The modal allows users to accept cookies or manage cookie settings.
 */
export class WcConsentModal extends WebComponent_Base {
	public instanceId: string;
	private lastFocusedElement: Element | null = null;
	private handleKeydown!: (event: Event) => void;

	constructor() {
		super();
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcConsentModal';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Perform additional setup after initialization.
	 */
	protected afterInitialize() {
		this.manageFocus();
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Adds event listeners for the buttons and close icon.
	 * Ensures that actions such as accepting cookies or managing cookies are handled.
	 */
	protected attachListeners(): void {
		const acceptCookiesBtn = this.shadowRoot!.getElementById('wc-consent-modal__acceptCookies');
		const rejectCookiesBtn = this.shadowRoot!.getElementById('wc-consent-modal__rejectCookies');
		const manageCookiesBtn = this.shadowRoot!.getElementById('wc-consent-modal__manageCookies');

		if (acceptCookiesBtn) {
			addEventListener(acceptCookiesBtn, 'click', this.acceptAllCookies.bind(this));
		}
		if (rejectCookiesBtn) {
			addEventListener(rejectCookiesBtn, 'click', this.rejectNonEssentialCookies.bind(this));
		}
		if (manageCookiesBtn) {
			addEventListener(manageCookiesBtn, 'click', this.handleManageCookies.bind(this));
		}

		// Add the keydown event listener for "Escape" key
		addEventListener(document, 'keydown', this.handleKeydown);
		document.body.classList.add('modal-active');
	}

	async focusAgeGateModal() {
		await customElements.whenDefined('wc-age-gate-modal');
		const ageGateModal = document.querySelector('wc-age-gate-modal') as HTMLElement | null;

		if (ageGateModal) {
			const ageGateModalShadowRoot = ageGateModal.shadowRoot?.querySelector(
				'.wc-age-gate-modal',
			) as HTMLElement | null;

			if (ageGateModalShadowRoot) {
				ageGateModalShadowRoot.focus();
				applyFocusTrap(ageGateModalShadowRoot);
			}
		}

		if (!ageGateModal && !document.body.querySelector('.modal-active')) {
			// Remove body class if no other modal is present
			document.body.classList.remove('modal-active');
		}
	}

	/**
	 * Accepts all cookies and closes the modal.
	 * Dispatches events to update consent for all types and closes the modal.
	 */
	async acceptAllCookies() {
		// Define the consent updates you want to apply
		const consentUpdates: ConsentUpdate[] = [
			{ consentType: 'essential', consentGiven: true },
			{ consentType: 'functional', consentGiven: true },
			{ consentType: 'marketing', consentGiven: true },
		];

		// Dispatch the event to notify listeners
		dispatchConsentUpdate(consentUpdates);
		this.closeModal();
		this.focusAgeGateModal();
	}

	/**
	 * Rejects non-essential cookies and closes the modal.
	 * Dispatches events to update consent so only essential cookies are allowed.
	 */
	async rejectNonEssentialCookies() {
		// Define the consent updates you want to apply
		const consentUpdates: ConsentUpdate[] = [
			{ consentType: 'essential', consentGiven: true },
			{ consentType: 'functional', consentGiven: false },
			{ consentType: 'marketing', consentGiven: false },
		];

		// Dispatch the event to notify listeners
		dispatchConsentUpdate(consentUpdates);
		this.closeModal();
		this.focusAgeGateModal();
	}

	/**
	 * Closes the modal and restores focus to the last focused element.
	 * This method ensures that when the modal is dismissed, focus is returned to the
	 * previously focused element to maintain a logical focus flow.
	 * Also removes the keydown listener to prevent memory leaks.
	 * Also calls the function to remove the body 'modal-active' class.
	 */
	async closeModal() {
		this.remove();

		if (this.lastFocusedElement) {
			(this.lastFocusedElement as HTMLElement).focus();
		}

		// Remove the keydown event listener when the modal is closed
		removeEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Manages focus within the modal to ensure accessibility.
	 * Uses `requestAnimationFrame` to delay focus setting until after the browser has had a chance
	 * to render any updates, preventing potential layout thrashing.
	 */
	manageFocus() {
		this.lastFocusedElement = document.activeElement;
		const consentModal = this.shadowRoot!.querySelector('.wc-consent-modal') as HTMLElement;
		if (consentModal) {
			consentModal.focus(); // Set focus to the modal when it appears
			applyFocusTrap(consentModal); // Use the utility function to apply focus trapping
		}
	}

	/**
	 * Handles the action of managing cookies.
	 * Closes the current consent modal and opens the wcConsentManage modal.
	 * This function ensures that no cookies are set during the modal transition.
	 */
	handleManageCookies() {
		// Create and append the wcConsentManage modal to the DOM
		const consentManageModal = document.createElement('wc-consent-manage-modal') as HTMLElement;
		const consentModal = this.shadowRoot!.querySelector('.wc-consent-modal') as HTMLElement;

		if (!consentModal) return;

		consentModal.classList.remove('modal-active');
		document.body.appendChild(consentManageModal);

		requestAnimationFrame(() => {
			const consentManageModalShadowRoot = consentManageModal.shadowRoot as ShadowRoot;
			const consentManageModalElem = consentManageModalShadowRoot.querySelector(
				'.wc-consent-manage-modal',
			) as HTMLElement;

			if (!consentManageModalShadowRoot && !consentManageModalElem) return;

			setTimeout(function () {
				consentManageModalElem?.classList.add('modal-active', 'modal-transition');
				consentManageModalElem?.addEventListener(
					'transitionend',
					() => {
						// Shouldn't need a timeout, but I think there was an issue with the web component
						// loading with the transition simultaneously and this transitionend firing prematurely
						setTimeout(function () {
							consentManageModalElem?.classList.remove('modal-transition');
						}, 500);
					},
					{ once: true },
				);
			}, 1);

			consentManageModal.focus();
			applyFocusTrap(consentManageModal);
		});
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-consent-modal', WcConsentModal);
